import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Activity, Copy, Download, PlayCircle, Server, Database, Boxes, ShieldCheck, ScrollText, Mail } from "lucide-react";
import ReAuthModal from "../components/ReAuthModal";
import { useAuth } from "../context/AuthContext";

export default function SystemCenter() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [report, setReport] = useState(null);
  const [health, setHealth] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testEmailReauthOpen, setTestEmailReauthOpen] = useState(false);

  const loadReport = () => api.get("/system/report").then((r) => setReport(r.data));
  useEffect(() => { loadReport(); }, []);

  const runHealth = async () => {
    setBusy(true);
    try { const { data } = await api.get("/system/health"); setHealth(data); toast.success(`Sağlık kontrolü: ${data.overall}`); }
    catch (e) { toast.error("Kontrol başarısız"); }
    finally { setBusy(false); }
  };

  const runReadiness = async () => {
    setBusy(true);
    try { const { data } = await api.get("/system/readiness"); setReadiness(data); toast.success(`Yayına Hazırlık: ${data.overall}`); }
    catch (e) { toast.error("Kontrol başarısız"); }
    finally { setBusy(false); }
  };

  const runTestEmail = async (reauthToken) => {
    setBusy(true);
    try {
      const { data } = await api.post("/system/test-email", { to: testEmailTo.trim() || undefined }, { headers: { "X-Reauth-Token": reauthToken } });
      toast.success(`Test e-postası gönderildi: ${data.sent_to}`);
      runReadiness();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test e-postası gönderilemedi");
    } finally { setBusy(false); }
  };

  const toMarkdown = () => {
    if (!report) return "";
    const md = [];
    md.push(`# RIVACO Teknik Raporu\n`);
    md.push(`**Sürüm:** ${report.version} (${report.build})  `);
    md.push(`**Tarih:** ${report.generated_at}  `);
    md.push(`**Environment:** ${report.environment}\n`);
    md.push(`## Teknoloji\n- Frontend: ${report.tech.frontend}\n- Backend: ${report.tech.backend}\n- Database: ${report.tech.database}\n- Auth: ${report.tech.auth}\n- Kütüphaneler: ${report.tech.libraries.join(", ")}\n`);
    md.push(`## Runtime\n- Aktif kullanıcı: ${report.runtime.active_users}\n- Açık vardiya: ${report.runtime.open_shifts}\n- Aktif session: ${report.runtime.active_sessions}\n`);
    md.push(`## Modüller`);
    report.modules.forEach((m) => md.push(`- **${m.name}** ${m.version}${m.depends_on.length ? ` — depends: ${m.depends_on.join(", ")}` : ""}`));
    md.push(`\n## RBAC Matrisi\n| Özellik | Owner | Manager | Cashier |\n|---|---|---|---|`);
    report.rbac.forEach((r) => md.push(`| ${r.feature} | ${fmt(r.owner)} | ${fmt(r.manager)} | ${fmt(r.cashier)} |`));
    md.push(`\n## Collections`);
    report.collections.forEach((c) => md.push(`- \`${c.name}\` (${c.count} kayıt) — indexes: ${c.indexes.join(", ")}`));
    md.push(`\n## Endpoints (${report.endpoints.length})`);
    report.endpoints.forEach((e) => md.push(`- ${e.methods.join(",")} \`${e.path}\` — rol: ${e.role}`));
    md.push(`\n## Changelog`);
    report.changelog.forEach((c) => {
      md.push(`### ${c.version} — ${c.date}`);
      if (c.added?.length) md.push(`**Eklendi:** ${c.added.join(" · ")}`);
      if (c.changed?.length) md.push(`**Değişti:** ${c.changed.join(" · ")}`);
      if (c.fixed?.length) md.push(`**Düzeltildi:** ${c.fixed.join(" · ")}`);
    });
    md.push(`\n## Güvenlik Notları\n${report.security_notes.map((s) => "- " + s).join("\n")}`);
    md.push(`\n> ⚠️ Bu rapor hiçbir secret / token / şifre içermez.`);
    return md.join("\n");
  };

  const copyReport = async () => { try { await navigator.clipboard.writeText(toMarkdown()); toast.success("Panoya kopyalandı"); } catch { toast.error("Kopyalanamadı"); } };
  const downloadMd = () => {
    const blob = new Blob([toMarkdown()], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `rivaco-tech-report-${report.version}.md`; a.click(); URL.revokeObjectURL(url);
  };
  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `rivaco-tech-report-${report.version}.json`; a.click(); URL.revokeObjectURL(url);
  };

  if (!report) return <div className="text-stone-500">Yükleniyor…</div>;

  const tabs = [
    { k: "overview", label: "Genel", icon: Server },
    { k: "changelog", label: "Güncelleme Geçmişi", icon: ScrollText },
    { k: "modules", label: "Modüller", icon: Boxes },
    { k: "rbac", label: "Yetki Matrisi", icon: ShieldCheck },
    { k: "db", label: "Veri Yapısı", icon: Database },
    { k: "api", label: "API Envanteri", icon: Activity },
    { k: "health", label: "Sağlık", icon: PlayCircle },
    { k: "readiness", label: "Yayına Hazırlık", icon: ShieldCheck },
  ];

  return (
    <div data-testid="system-root" className="max-w-6xl">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">SADECE OWNER</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900">Sistem Merkezi</h1>
          <div className="text-xs text-stone-500 font-mono-code mt-1">
            RIVACO {report.version} · {report.build} · {report.environment} · {new Date(report.generated_at).toLocaleString("tr-TR")}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="btn-copy-report" onClick={copyReport} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-stone-200 text-sm hover:bg-stone-50"><Copy className="w-4 h-4" /> Panoya Kopyala</button>
          <button data-testid="btn-export-md" onClick={downloadMd} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg gold-gradient text-white text-sm"><Download className="w-4 h-4" /> Markdown</button>
          <button data-testid="btn-export-json" onClick={downloadJson} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-stone-200 text-sm hover:bg-stone-50"><Download className="w-4 h-4" /> JSON</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-4">
        {tabs.map((t) => (
          <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)} className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs ${tab === t.k ? "bg-stone-900 text-white" : "border border-stone-200 text-stone-700 hover:bg-stone-50"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Sürüm" v={report.version} />
          <KPI label="Build" v={report.build} />
          <KPI label="Aktif Kullanıcı" v={report.runtime.active_users} />
          <KPI label="Açık Vardiya" v={report.runtime.open_shifts} />
          <KPI label="Aktif Session" v={report.runtime.active_sessions} />
          <KPI label="Frontend" v={report.tech.frontend.split(" + ")[0]} />
          <KPI label="Backend" v={report.tech.backend.split(" + ")[0]} />
          <KPI label="Database" v={report.tech.database} />
        </div>
      )}

      {tab === "changelog" && (
        <div className="space-y-3">
          {report.changelog.map((c) => (
            <div key={c.version} className="card-lux p-4">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-serif-display text-xl text-amber-800">{c.version}</span>
                <span className="text-xs font-mono-code text-stone-500">{c.date}</span>
              </div>
              {c.added?.length > 0 && <Item label="Eklendi" tone="emerald" items={c.added} />}
              {c.changed?.length > 0 && <Item label="Değişti" tone="amber" items={c.changed} />}
              {c.fixed?.length > 0 && <Item label="Düzeltildi" tone="stone" items={c.fixed} />}
            </div>
          ))}
        </div>
      )}

      {tab === "modules" && (
        <div className="card-lux overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-stone-50 border-b border-stone-200"><tr className="text-xs uppercase text-stone-500"><th className="px-3 py-2 text-left">Modül</th><th className="px-3 py-2 text-left">Sürüm</th><th className="px-3 py-2 text-left">Bağımlılıklar</th></tr></thead>
            <tbody className="divide-y divide-stone-100">
              {report.modules.map((m) => (
                <tr key={m.name}><td className="px-3 py-2 text-sm">{m.name}</td><td className="px-3 py-2 text-xs font-mono-code text-amber-700">{m.version}</td><td className="px-3 py-2 text-xs text-stone-500">{m.depends_on.join(", ") || "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "rbac" && (
        <div className="card-lux overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-stone-50 border-b border-stone-200"><tr className="text-xs uppercase text-stone-500"><th className="px-3 py-2 text-left">Özellik</th><th className="px-3 py-2 text-center">Owner</th><th className="px-3 py-2 text-center">Manager</th><th className="px-3 py-2 text-center">Cashier</th></tr></thead>
            <tbody className="divide-y divide-stone-100">
              {report.rbac.map((r) => (
                <tr key={r.feature}><td className="px-3 py-2 text-sm">{r.feature}</td><td className="px-3 py-2 text-center text-sm">{fmt(r.owner)}</td><td className="px-3 py-2 text-center text-sm">{fmt(r.manager)}</td><td className="px-3 py-2 text-center text-sm">{fmt(r.cashier)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "db" && (
        <div className="card-lux overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-stone-50 border-b border-stone-200"><tr className="text-xs uppercase text-stone-500"><th className="px-3 py-2 text-left">Collection</th><th className="px-3 py-2 text-right">Kayıt</th><th className="px-3 py-2 text-left">Index'ler</th></tr></thead>
            <tbody className="divide-y divide-stone-100">
              {report.collections.map((c) => (
                <tr key={c.name}><td className="px-3 py-2 text-sm font-mono-code">{c.name}</td><td className="px-3 py-2 text-right text-sm font-mono-code">{c.count}</td><td className="px-3 py-2 text-xs text-stone-500 font-mono-code">{c.indexes.join(", ")}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="p-3 text-[11px] text-stone-500">Password hash, PIN hash ve secret'lar burada gösterilmez.</div>
        </div>
      )}

      {tab === "api" && (
        <div className="card-lux overflow-hidden max-h-[70vh] overflow-y-auto">
          <table className="min-w-full">
            <thead className="bg-stone-50 border-b border-stone-200 sticky top-0"><tr className="text-xs uppercase text-stone-500"><th className="px-3 py-2 text-left">Method</th><th className="px-3 py-2 text-left">Path</th><th className="px-3 py-2 text-left">Rol</th></tr></thead>
            <tbody className="divide-y divide-stone-100">
              {report.endpoints.map((e, i) => (
                <tr key={i}><td className="px-3 py-2 text-xs font-mono-code text-amber-700">{e.methods.join(",")}</td><td className="px-3 py-2 text-xs font-mono-code">{e.path}</td><td className="px-3 py-2 text-xs text-stone-500">{e.role}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "health" && (
        <div className="card-lux p-5">
          <button data-testid="btn-run-health" onClick={runHealth} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60 mb-4"><PlayCircle className="w-4 h-4" /> Sistem Kontrolünü Çalıştır</button>
          {health && (
            <>
              <div className={`inline-flex text-xs px-3 py-1 rounded-full border font-medium mb-3 ${health.overall === "PASS" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : health.overall === "WARNING" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-red-100 text-red-800 border-red-200"}`}>Genel: {health.overall}</div>
              <ul className="space-y-1 text-sm">
                {health.checks.map((c, i) => (
                  <li key={i} className="flex items-center justify-between border border-stone-200 rounded-lg px-3 py-2">
                    <span>{c.name}</span>
                    <span className={`text-xs font-mono-code ${c.status === "PASS" ? "text-emerald-700" : c.status === "WARNING" ? "text-amber-700" : "text-red-700"}`}>{c.status}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {tab === "readiness" && (
        <div className="card-lux p-5" data-testid="readiness-panel">
          <div className="flex flex-wrap gap-2 mb-4">
            <button data-testid="btn-run-readiness" onClick={runReadiness} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60"><ShieldCheck className="w-4 h-4" /> Yayına Hazırlık Kontrolü</button>
            <div className="flex items-center gap-2 flex-1 min-w-[220px]">
              <input data-testid="test-email-to" type="email" value={testEmailTo} onChange={(e) => setTestEmailTo(e.target.value)} placeholder={user?.email || "test@rivaco.com"} className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-sm outline-none focus:border-amber-400" />
              <button data-testid="btn-send-test-email" onClick={() => setTestEmailReauthOpen(true)} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm disabled:opacity-60 hover:bg-amber-100"><Mail className="w-4 h-4" /> Test E-postası Gönder</button>
            </div>
          </div>
          {readiness && (
            <>
              <div className={`inline-flex text-xs px-3 py-1 rounded-full border font-medium mb-3 ${readiness.overall === "READY" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : readiness.overall === "WARNING" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-red-100 text-red-800 border-red-200"}`} data-testid="readiness-badge">
                {readiness.overall === "READY" ? "YAYINA HAZIR" : readiness.overall === "WARNING" ? "UYARILAR VAR" : "YAYINA HAZIR DEĞİL"}
              </div>
              <ul className="space-y-1 text-sm">
                {readiness.checks.map((c, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 border border-stone-200 rounded-lg px-3 py-2" data-testid={`readiness-check-${c.key}`}>
                    <div className="flex-1">
                      <div className="text-sm">{c.label}</div>
                      {c.detail && <div className="text-[11px] text-stone-500 mt-0.5">{c.detail}</div>}
                    </div>
                    <span className={`text-xs font-mono-code shrink-0 ${c.status === "PASS" ? "text-emerald-700" : c.status === "WARNING" ? "text-amber-700" : "text-red-700"}`}>{c.status}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {!readiness && <div className="text-sm text-stone-500">Kontrol çalıştırın.</div>}
          <ReAuthModal open={testEmailReauthOpen} scope="test_email" title="Test e-postası göndermek için Owner şifre + Authenticator kodu gerekli." ownerHasTotp={!!user?.totp_enabled} onClose={() => setTestEmailReauthOpen(false)} onSuccess={(tok) => { setTestEmailReauthOpen(false); runTestEmail(tok); }} />
        </div>
      )}
    </div>
  );
}

function KPI({ label, v }) { return <div className="card-lux p-3"><div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div><div className="mt-1 font-serif-display text-lg text-stone-900">{v}</div></div>; }
function Item({ label, tone, items }) {
  const tones = { emerald: "text-emerald-700", amber: "text-amber-700", stone: "text-stone-700" };
  return <div className="mt-1 text-sm"><span className={`font-semibold ${tones[tone]}`}>{label}:</span> <span className="text-stone-700">{items.join(" · ")}</span></div>;
}
function fmt(v) { if (v === true) return "✓"; if (v === false) return "×"; return String(v); }
